import { Controller } from "@hotwired/stimulus"

/**
 * Bag Checkout State Controller
 * 
 * Manages state persistence and restoration for the checkout flow.
 * Handles:
 * - Restoring checkout state on page load
 * - Marking sections as completed
 * - Managing section completion state
 */
export default class extends Controller {
    static targets = ["section"]

    // Completion state is initialized from the persisted order in
    // index.html.erb and kept in sync after AJAX saves/edits. Assigning these
    // Stimulus values also updates the data attributes, so Turbo snapshots
    // restore the latest progression state. Field values must NOT be used as
    // completion evidence: browser autofill / form restoration can populate
    // email and name before this controller runs and skip the AJAX saves.
    static values = {
        contactComplete: { type: Boolean, default: false },
        shippingComplete: { type: Boolean, default: false }
    }

    connect() {
        //console.log('Bag checkout state controller connected');
        
        // Restore state from form fields on page load
        // Use setTimeout to ensure all controllers are connected first
        setTimeout(() => this.restoreCheckoutState(), 0);
        
        // Listen for section completion events from main controller. Keep the
        // bound reference so disconnect() removes the same function it added.
        this.boundSectionCompleted = this.handleSectionCompleted.bind(this);
        this.boundSectionEditing = this.handleSectionEditing.bind(this);
        this.element.addEventListener('bag--checkout:sectionCompleted', this.boundSectionCompleted);
        this.element.addEventListener('bag--checkout:sectionEditing', this.boundSectionEditing);
    }

    disconnect() {
        this.element.removeEventListener('bag--checkout:sectionCompleted', this.boundSectionCompleted);
        this.element.removeEventListener('bag--checkout:sectionEditing', this.boundSectionEditing);
    }

    /**
     * Restore checkout state from the server-provided completion values.
     * The email field value is read only for the summary display — when
     * contact is complete it holds the server-rendered order email.
     */
    restoreCheckoutState() {
        const email = this.element.querySelector('[name="order[email]"]')?.value || '';

        if (this.contactCompleteValue) {
            this.completeSection(1, email);

            if (this.shippingCompleteValue) {
                this.completeSection(2, '');

                // Dispatch event to open payment section (skip scroll on initial load)
                this.dispatch('restoreComplete', {
                    detail: { openSection: 3, skipScroll: true }
                });
            } else {
                // Open shipping section since contact is done (skip scroll on initial load)
                this.dispatch('restoreComplete', {
                    detail: { openSection: 2, skipScroll: true }
                });
            }
        } else {
            // New checkout - open Section 1 (Contact Information)
            this.dispatch('restoreComplete', {
                detail: { openSection: 1, skipScroll: true }
            });
        }
    }

    /**
     * Handle section completion events
     */
    handleSectionCompleted(event) {
        const { section, email } = event.detail;

        if (section === 1) this.contactCompleteValue = true;
        if (section === 2) this.shippingCompleteValue = true;

        this.completeSection(section, email || '');
    }

    /**
     * Editing invalidates the edited step and every dependent later step in
     * the cached DOM. The persisted server values remain unchanged until the
     * shopper saves again, but a Turbo history restore must reopen the edit
     * rather than silently using unsaved fields with stale wallet totals.
     */
    handleSectionEditing(event) {
        const { section } = event.detail;

        if (section === 1) this.contactCompleteValue = false;
        if (section <= 2) this.shippingCompleteValue = false;
    }

    /**
     * Mark a section as completed
     */
    completeSection(sectionNum, summary) {
        const section = this.sectionTargets[sectionNum - 1];
        if (!section) return;
        
        const sectionBody = section.querySelector('[data-bag--checkout-target="sectionBody"]');
        const sectionHeader = section.querySelector('[data-bag--checkout-target="sectionHeader"]');
        const editLink = section.querySelector(`[data-bag--checkout-target="editLink${sectionNum}"]`);
        
        // Add completed class and remove active class
        section.classList.add('completed');
        section.classList.remove('active');
        
        // Hide body
        if (sectionBody) {
            sectionBody.style.display = 'none';
        }
        
        // Update ARIA attributes
        if (sectionHeader) {
            sectionHeader.setAttribute('aria-expanded', 'false');
        }
        
        // Show edit link
        if (editLink) {
            editLink.style.display = 'inline';
            editLink.innerHTML = '<i class="bi bi-pencil me-1" aria-hidden="true"></i>Edit';
        }
        
        // Dispatch event for summary controller to show summary
        this.dispatch('showSummary', { 
            detail: { section: sectionNum, summary } 
        });
    }
}
